import { useAgeGenderStepStatus } from '@/hooks/use-age-gender-step-status';
import {
    getConsentContext,
    getNextConsentType,
} from '@/hooks/use-consent-context';
import { useConsents } from '@/hooks/use-consents';
import { AccountAccount, CircleCircle } from '@calm/evexia-web-sdk';
import useAccount from '@rhg/hooks/use-account';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import {
    ConsentAcceptance,
    ConsentFlow,
    ConsentRequirement,
    ConsentTypes,
} from '@rhg/hooks/use-consent-context/types';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useNotificationUser } from '@rhg/hooks/use-notification-user';
import {
    getConsentRegistrationStepMiraculouslyWorks,
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { logger } from '@rhg/libs/logger';
import { useCallback, useMemo } from 'react';
import { getNeedsEmailOptInResponse } from '../use-needs-email-opt-in-response';

interface GetNextStepParams {
    registrationFlow?: RegistrationFlow;
    circle?: CircleCircle;
    account?: AccountAccount;
    consentRequirements?: ConsentRequirement;
    consentAcceptance?: ConsentAcceptance;
    consentFlow?: ConsentTypes[];
}

/**
 * Basically, the flow for all of the registration pages on submit is:
 *   1. call update APIs
 *        (For consents, this occurs within ConsentStep > onAgree).
 *   2. refetch changed dependencies
 *         (Usually one or more of account, circle, account consents, and sponsorship).
 *   3. use response from the update + the re-fetched dependencies to figure out the next step using:
 *        a. getNextStep() for post eligibility assignment (Age/Gender is always post-eligibility assignment)
 *        b. getConsentRegistrationStep() (for pre-eligibility).
 *        c. For consents, this is done in the ConsentStep > onAgree.
 */
export function usePostSubmitStep() {
    const { activeCircle, isFetched: isActiveCircleFetched } =
        useActiveCircle();
    const { account: cachedAccount, isFetched: isAccountFetched } =
        useAccount();
    const { notificationUser, isFetched: isNotificationsDataFetched } =
        useNotificationUser();

    const { consents, isLoading: isGetConsentsLoading } = useConsents();
    const { consent, isFetched: isConsentFetched } = useHasAcceptedCohortTos();
    const { getHasCompletedAgeGenderStep } = useAgeGenderStepStatus();

    // Account consent is used when the user is unsponsored
    const accountConsents = consent?.consentData ? [consent.consentData] : [];
    const accountUserConsents = consent?.userResponse
        ? [consent.userResponse]
        : [];

    // No currentSponsor or shouldUseCurrentSponsor since we are now on our new sponsorship
    const cachedConsentRequirements = getConsentContext({
        accountConsents,
        accountUserConsents,
        sponsorConsents: consents,
    });
    const isFetched = useMemo(
        () =>
            isConsentFetched &&
            !isGetConsentsLoading &&
            isAccountFetched &&
            isActiveCircleFetched &&
            isNotificationsDataFetched,
        [
            isAccountFetched,
            isActiveCircleFetched,
            isConsentFetched,
            isGetConsentsLoading,
            isNotificationsDataFetched,
        ]
    );

    /** Only call this if either this hook's `isFetched === true` or if you are passing in circle, account, and consentRequirements to this function */
    const getNextStep = useCallback(
        ({
            registrationFlow,
            circle,
            account,
            consentRequirements,
            consentAcceptance,
            consentFlow,
        }: GetNextStepParams = {}) => {
            if ((!circle || !account || !consentRequirements) && !isFetched) {
                logger.error(
                    'getNextStep: Circle, account, and consentRequirements are required to get the next step unless prerequisites have been fetched',
                    {
                        isFetched,
                        circle: !!circle,
                        account: !!account,
                        consentRequirements: !!consentRequirements,
                    },
                    new Error(
                        'getNextStep: Circle, account, and consentRequirements are required to get the next step unless prerequisites have been fetched'
                    )
                );
                /**
                 * When they continue from here, they may see another registration step since they should be caught by the UserBlockerGate
                 * This is a better fallback than just erroring the user out
                 */
                return RegistrationFlowStep.Success;
            }

            if (!(account ?? cachedAccount)?.countryCode) {
                return RegistrationFlowStep.SelectCountry;
            }

            const nextConsentStep = getConsentRegistrationStepMiraculouslyWorks(
                getNextConsentType({
                    consentRequirements:
                        consentRequirements ?? cachedConsentRequirements,
                    consentAcceptance,
                    consentFlow: consentFlow ?? ConsentFlow,
                })
            );

            if (nextConsentStep) {
                return nextConsentStep;
            }

            if (
                getHasCompletedAgeGenderStep() !== 'true' &&
                registrationFlow !== RegistrationFlow.LossOfSponsorship
            ) {
                return RegistrationFlowStep.AgeGender;
            }

            return getNeedsEmailOptInResponse(
                circle ?? activeCircle,
                notificationUser
            )
                ? RegistrationFlowStep.EmailCommunicationPreferences
                : RegistrationFlowStep.Success;
        },
        [
            isFetched,
            cachedAccount,
            getHasCompletedAgeGenderStep,
            cachedConsentRequirements,
            activeCircle,
            notificationUser,
        ]
    );

    return {
        isFetched,
        getNextStep,
    };
}
